<script setup>
import { useDialogPluginComponent } from 'quasar';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import HrBasicDialog from '../HrBasicDialog.vue';
import HrBtn from '../HrBtn.vue';
import HrForm from '../HrForm.vue';
import HrTextField from '../HrTextField.vue';

/**
 * Text input dialog presented via promptDialog() with optional validation.
 *
 * @status internal
 * @group cards-dialogs
 */
defineOptions({ name: 'HrPromptDialog' });

const props = defineProps({
  /** Dialog headline. */
  title: {
    type: String,
    required: true,
  },
  /** Supporting text providing context for the prompt. */
  message: {
    type: String,
    required: true,
  },
  /** Initial value pre-filled into the input. */
  model: {
    type: String,
    default: '',
  },
  /** Label shown inside the text field. */
  label: {
    type: String,
    default: null,
  },
  /** HTML input type forwarded to the text field (e.g. 'email', 'text', 'number'). */
  type: {
    type: String,
    default: 'text',
  },
  /**
   * Quasar-style validation rules forwarded to the text field.
   * Each rule is a function returning true or an error string.
   * The confirm button is also disabled while any rule fails.
   * Prefer this over isValid when an inline error message is needed.
   */
  rules: {
    type: Array,
    default: null,
  },
  /**
   * When true, field validation only runs after the first blur.
   * The confirm button guard still evaluates rules on every keystroke.
   */
  lazyRules: {
    type: [Boolean, String],
    default: false,
  },
  /**
   * Returns true when the current input value is acceptable.
   * The confirm button is disabled while this returns false.
   * Use isValid for a simple guard with no inline error message;
   * use rules when an error message in the field is also needed.
   */
  isValid: {
    type: Function,
    default: null,
  },
  /** Label for the confirm action button. Defaults to the common.buttonConfirm translation. */
  confirmLabel: {
    type: String,
    default: null,
  },
  /** Label for the cancel action button. Defaults to the common.buttonCancel translation. */
  cancelLabel: {
    type: String,
    default: null,
  },
  /**
   * Prevents dismissal via backdrop click or Escape key.
   * Defaults to true — a prompt dialog requires an explicit choice.
   */
  persistent: {
    type: Boolean,
    default: true,
  },
});

defineEmits([...useDialogPluginComponent.emits]);

const { dialogRef, onDialogHide, onDialogOK, onDialogCancel } = useDialogPluginComponent();

const { t } = useI18n();
const effectiveConfirmLabel = computed(() => props.confirmLabel ?? t('common.buttonConfirm'));
const effectiveCancelLabel = computed(() => props.cancelLabel ?? t('common.buttonCancel'));

const value = ref(props.model);
const isConfirmDisabled = computed(() => props.isValid !== null && !props.isValid(value.value));
</script>

<template>
  <hr-basic-dialog
    ref="dialogRef"
    :persistent="persistent"
    @hide="onDialogHide"
  >
    <div
      class="hr-dialog"
      data-e2e="dialog-prompt"
    >
      <p class="hr-dialog__headline hr-headline-small">
        {{ title }}
      </p>
      <p class="hr-dialog__supporting-text hr-body-medium">
        {{ message }}
      </p>
      <hr-form @submit="onDialogOK(value)">
        <hr-text-field
          v-model="value"
          class="hr-dialog__input"
          :label="label"
          :type="type"
          :rules="rules"
          :lazy-rules="lazyRules"
          autofocus
          data-e2e="input-prompt"
        />
        <div class="hr-dialog__actions">
          <!-- @todo When the legacy theme is removed: remove :legacy-props -->
          <hr-btn
            variant="text"
            :label="effectiveCancelLabel"
            :legacy-props="{ flat: true, color: 'secondary' }"
            data-e2e="button-cancel"
            @click="() => onDialogCancel()"
          />
          <hr-btn
            type="submit"
            variant="text"
            :label="effectiveConfirmLabel"
            :disable="isConfirmDisabled"
            :legacy-props="{ flat: true, color: 'primary' }"
            data-e2e="button-confirm"
          />
        </div>
      </hr-form>
    </div>
  </hr-basic-dialog>
</template>

<style lang="scss" scoped>
.hr-dialog {
  background: var(--app-surface-container-high);
  border-radius: var(--app-shape-corner-extra-large);
  padding: 24px;
  display: flex;
  flex-direction: column;
}

.hr-dialog__headline {
  color: var(--app-on-surface);
  margin: 0 0 16px;
}

.hr-dialog__supporting-text {
  color: var(--app-on-surface-variant);
  margin: 0 0 16px;
  /* Supporting text is plain text; honor authored line breaks (\n) without allowing HTML. */
  white-space: pre-line;
}

.hr-dialog__input {
  width: 100%;
}

.hr-dialog__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 24px;
}
</style>
